import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import DynamicSidebar from '../../components/ui/DynamicSidebar';
import DashboardHeader from '../../components/ui/DashboardHeader';
import Icon from '../../components/AppIcon';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';

const STATUS_MAP = {
  0: { label: 'Pending', color: 'bg-warning/10 text-warning' },
  1: { label: 'Success', color: 'bg-success/10 text-success' },
  2: { label: 'Processing', color: 'bg-primary/10 text-primary' },
  3: { label: 'Failed', color: 'bg-error/10 text-error' },
  4: { label: 'Reversed', color: 'bg-muted text-text-secondary' },
};

const STATUS_STRING_MAP = {
  SUCCESS: { label: 'Success', color: 'bg-success/10 text-success' },
  FAILED: { label: 'Failed', color: 'bg-error/10 text-error' },
  PENDING: { label: 'Pending', color: 'bg-warning/10 text-warning' },
  PROCESSING: { label: 'Processing', color: 'bg-primary/10 text-primary' },
  REVERSED: { label: 'Reversed', color: 'bg-muted text-text-secondary' },
};

const SETTLEMENT_STATUS_MAP = {
  SETTLED: { label: 'Settled', color: 'bg-success/10 text-success' },
  UNSETTLED: { label: 'Unsettled', color: 'bg-warning/10 text-warning' },
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const TransactionsPage = ({ paymentType = 'deposit' }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    total: 0,
    totalPages: 0,
    firstPage: true,
    lastPage: false,
  });
  const [filters, setFilters] = useState({
    status: '',
    client_reference: '',
    gw_reference: '',
    acquirer_reference: '',
    service_code: '',
    start_date: '',
    end_date: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const navigate = useNavigate();
  const { merchants } = useAuth();

  const fetchTransactions = useCallback(async (page = 0, size = pagination?.size) => {
    setLoading(true);
    setError('');
    try {
      const activeFilters = Object.fromEntries(
        Object.entries(filters)?.filter(([, v]) => v !== '')
      );
      const qs = new URLSearchParams();
      qs?.append('page', page);
      qs?.append('size', size);
      qs?.append('payment_type', paymentType);
      Object.entries(activeFilters)?.forEach(([k, v]) => qs?.append(k, v));
      const merchantsList = Array.isArray(merchants) && merchants?.length > 0 ? merchants : [];
      merchantsList?.forEach(m => qs?.append('merchants', m));
      const response = await apiService?.get(`/portal/transactions?${qs?.toString()}`);
      const data = response?.data;
      const details = data?.details?.[0];
      const records = details?.records || [];
      setTransactions(records);
      setPagination(prev => ({
        ...prev,
        page: details?.current_page ?? page,
        size,
        total: details?.total_records ?? 0,
        totalPages: details?.total_pages ?? 0,
        firstPage: details?.first_page ?? true,
        lastPage: details?.last_page ?? false,
      }));
    } catch (err) {
      setError(err?.response?.data?.status_description || err?.response?.data?.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [filters, paymentType, merchants]);

  useEffect(() => {
    fetchTransactions(0, pagination?.size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentType]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    fetchTransactions(0, pagination?.size);
  };

  const handleClearFilters = () => {
    const cleared = { status: '', client_reference: '', gw_reference: '', acquirer_reference: '', service_code: '', start_date: '', end_date: '' };
    setFilters(cleared);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 0 || newPage >= pagination?.totalPages) return;
    fetchTransactions(newPage, pagination?.size);
  };

  const handleSizeChange = (newSize) => {
    fetchTransactions(0, Number(newSize));
  };

  const handleRowClick = (tx) => {
    navigate(`/transactions/detail/${tx?.client_reference || tx?.gateway_reference || 'unknown'}`, {
      state: { transaction: tx, paymentType },
    });
  };

  const activeFiltersCount = Object.values(filters)?.filter(v => v !== '')?.length;

  const inputClass = 'h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring w-full';

  const getStatusBadge = (status) => {
    if (typeof status === 'string') {
      const s = STATUS_STRING_MAP?.[status?.toUpperCase()];
      if (s) return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s?.color}`}>{s?.label}</span>;
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-text-secondary">{status ?? '-'}</span>;
    }
    const s = STATUS_MAP?.[status];
    if (s) return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s?.color}`}>{s?.label}</span>;
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-text-secondary">{status ?? '-'}</span>;
  };

  const getSettlementBadge = (settlementStatus) => {
    const s = SETTLEMENT_STATUS_MAP?.[settlementStatus?.toUpperCase?.()];
    if (s) return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s?.color}`}>{s?.label}</span>;
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-text-secondary">{settlementStatus ?? '-'}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return format(new Date(dateStr), 'MMM dd, yyyy HH:mm'); } catch { return dateStr; }
  };

  const columns = [
    { key: 'merchant', label: 'Merchant' },
    { key: 'customer_name', label: 'Customer Name' },
    { key: 'mobile_number', label: 'Mobile' },
    { key: 'email', label: 'Email' },
    { key: 'service_name', label: 'Service Name' },
    { key: 'currency', label: 'Currency' },
    { key: 'amount', label: 'Amount' },
    { key: 'client_reference', label: 'Client Ref' },
    { key: 'gateway_reference', label: 'GW Ref' },
    { key: 'acquirer_reference', label: 'Acquirer Ref' },
    { key: 'status', label: 'Status' },
    { key: 'settlement_status', label: 'Settlement' },
    { key: 'created_at', label: 'Date' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <DynamicSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        <div className="p-6">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-text-primary">
                {paymentType === 'deposit' ? 'Deposits' : 'Payouts'}
              </h1>
              <p className="text-text-secondary mt-1 text-sm">
                {pagination?.total > 0 ? `${pagination?.total} total records` : 'No records found'}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border text-sm font-medium transition-smooth ${
                  showFilters || activeFiltersCount > 0
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border text-text-primary hover:bg-muted'
                }`}
              >
                <Icon name="Filter" size={16} />
                <span>Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="bg-primary-foreground text-primary text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => fetchTransactions(pagination?.page, pagination?.size)}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted"
              >
                <Icon name="RefreshCw" size={16} className="text-text-secondary" />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="bg-surface border border-border rounded-xl p-5 mb-5">
              <h3 className="text-sm font-semibold text-text-primary mb-4">Filter Transactions</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="text-xs font-medium text-text-secondary mb-1 block">Status</label>
                  <select
                    value={filters?.status}
                    onChange={e => handleFilterChange('status', e?.target?.value)}
                    className={inputClass}
                  >
                    <option value="">All Statuses</option>
                    <option value="0">0 - Pending</option>
                    <option value="1">1 - Success</option>
                    <option value="2">2 - Processing</option>
                    <option value="3">3 - Failed</option>
                    <option value="4">4 - Reversed</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-text-secondary mb-1 block">Client Reference</label>
                  <input
                    type="text"
                    value={filters?.client_reference}
                    onChange={e => handleFilterChange('client_reference', e?.target?.value)}
                    placeholder="Client Reference"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-text-secondary mb-1 block">GW Reference</label>
                  <input
                    type="text"
                    value={filters?.gw_reference}
                    onChange={e => handleFilterChange('gw_reference', e?.target?.value)}
                    placeholder="Gateway Reference"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-text-secondary mb-1 block">Acquirer Reference</label>
                  <input
                    type="text"
                    value={filters?.acquirer_reference}
                    onChange={e => handleFilterChange('acquirer_reference', e?.target?.value)}
                    placeholder="Acquirer Reference"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-text-secondary mb-1 block">Service Code</label>
                  <input
                    type="text"
                    value={filters?.service_code}
                    onChange={e => handleFilterChange('service_code', e?.target?.value)}
                    placeholder="e.g. D_MPESA_KE"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-text-secondary mb-1 block">Start Date</label>
                  <input
                    type="date"
                    value={filters?.start_date}
                    onChange={e => handleFilterChange('start_date', e?.target?.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-text-secondary mb-1 block">End Date</label>
                  <input
                    type="date"
                    value={filters?.end_date}
                    onChange={e => handleFilterChange('end_date', e?.target?.value)}
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleApplyFilters}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90"
                >
                  Apply Filters
                </button>
                <button
                  onClick={handleClearFilters}
                  className="px-4 py-2 border border-border text-text-primary rounded-lg text-sm hover:bg-muted"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-4 flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {/* Table */}
          <div className="bg-surface border border-border rounded-xl overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-max">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    {columns?.map(col => (
                      <th
                        key={col?.key}
                        className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide whitespace-nowrap"
                      >
                        {col?.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={columns?.length} className="text-center py-12">
                        <Icon name="Loader2" size={24} className="animate-spin text-primary mx-auto" />
                        <p className="text-text-secondary text-sm mt-2">Loading transactions...</p>
                      </td>
                    </tr>
                  ) : transactions?.length === 0 ? (
                    <tr>
                      <td colSpan={columns?.length} className="text-center py-12">
                        <div className="flex flex-col items-center space-y-2">
                          <Icon name="Inbox" size={32} className="text-text-secondary" />
                          <p className="text-text-secondary text-sm">No transactions found</p>
                          {activeFiltersCount > 0 && (
                            <button onClick={handleClearFilters} className="text-primary text-sm underline">
                              Clear filters
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    transactions?.map((tx, i) => (
                      <tr
                        key={tx?.client_reference || i}
                        onClick={() => handleRowClick(tx)}
                        onMouseEnter={() => setHoveredRow(tx?.client_reference || i)}
                        onMouseLeave={() => setHoveredRow(null)}
                        className="relative border-b border-border last:border-0 hover:bg-primary/5 active:bg-primary/10 transition-smooth cursor-pointer group"
                      >
                        <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap border-l-2 border-l-transparent group-hover:border-l-primary transition-all">{tx?.merchant || '-'}</td>
                        <td className="px-4 py-3 text-sm text-text-primary whitespace-nowrap">{[tx?.first_name, tx?.last_name]?.filter(Boolean)?.join(' ') || '-'}</td>
                        <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">{tx?.mobile_number || '-'}</td>
                        <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">{tx?.email || '-'}</td>
                        <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">{tx?.service?.service_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">{tx?.currency || '-'}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-text-primary whitespace-nowrap">
                          {tx?.amount != null ? Number(tx?.amount)?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-text-primary whitespace-nowrap">{tx?.client_reference || '-'}</td>
                        <td className="px-4 py-3 text-sm font-mono text-text-secondary whitespace-nowrap">{tx?.gateway_reference || '-'}</td>
                        <td className="px-4 py-3 text-sm font-mono text-text-secondary whitespace-nowrap">{tx?.acquirer_reference || '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{getStatusBadge(tx?.status)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{getSettlementBadge(tx?.settlement_status)}</td>
                        <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                          <div className="flex items-center justify-between gap-3">
                            <span>{formatDate(tx?.created_at)}</span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card List */}
            <div className="md:hidden">
              {loading ? (
                <div className="text-center py-12">
                  <Icon name="Loader2" size={24} className="animate-spin text-primary mx-auto" />
                  <p className="text-text-secondary text-sm mt-2">Loading transactions...</p>
                </div>
              ) : transactions?.length === 0 ? (
                <div className="text-center py-12">
                  <div className="flex flex-col items-center space-y-2">
                    <Icon name="Inbox" size={32} className="text-text-secondary" />
                    <p className="text-text-secondary text-sm">No transactions found</p>
                    {activeFiltersCount > 0 && (
                      <button onClick={handleClearFilters} className="text-primary text-sm underline">
                        Clear filters
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {transactions?.map((tx, i) => (
                    <div
                      key={tx?.client_reference || i}
                      onClick={() => handleRowClick(tx)}
                      className="p-4 hover:bg-primary/5 active:bg-primary/10 cursor-pointer transition-smooth border-l-2 border-l-transparent hover:border-l-primary"
                    >
                      {/* Top row: customer name + amount */}
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-sm font-semibold text-text-primary">
                            {[tx?.first_name, tx?.last_name]?.filter(Boolean)?.join(' ') || tx?.merchant || '-'}
                          </p>
                          <p className="text-xs text-text-secondary mt-0.5">{tx?.merchant || '-'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-text-primary">
                            {tx?.currency || ''} {tx?.amount != null ? Number(tx?.amount)?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                          </p>
                          <div className="mt-1">{getStatusBadge(tx?.status)}</div>
                        </div>
                      </div>

                      {/* Details grid */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-2">
                        {tx?.mobile_number && (
                          <div>
                            <p className="text-xs text-text-secondary">Mobile</p>
                            <p className="text-xs text-text-primary">{tx?.mobile_number}</p>
                          </div>
                        )}
                        {tx?.email && (
                          <div>
                            <p className="text-xs text-text-secondary">Email</p>
                            <p className="text-xs text-text-primary truncate">{tx?.email}</p>
                          </div>
                        )}
                        {tx?.service?.service_name && (
                          <div>
                            <p className="text-xs text-text-secondary">Service</p>
                            <p className="text-xs text-text-primary">{tx?.service?.service_name}</p>
                          </div>
                        )}
                        {tx?.settlement_status != null && (
                          <div>
                            <p className="text-xs text-text-secondary">Settlement</p>
                            <div className="mt-0.5">{getSettlementBadge(tx?.settlement_status)}</div>
                          </div>
                        )}
                        {tx?.client_reference && (
                          <div>
                            <p className="text-xs text-text-secondary">Client Ref</p>
                            <p className="text-xs font-mono text-text-primary truncate">{tx?.client_reference}</p>
                          </div>
                        )}
                        {tx?.gateway_reference && (
                          <div>
                            <p className="text-xs text-text-secondary">GW Ref</p>
                            <p className="text-xs font-mono text-text-secondary truncate">{tx?.gateway_reference}</p>
                          </div>
                        )}
                      </div>

                      {/* Date */}
                      <p className="text-xs text-text-secondary mt-2">{formatDate(tx?.created_at)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-border gap-3">
              <div className="flex items-center space-x-3">
                <span className="text-sm text-text-secondary">
                  Page {pagination?.page + 1} of {pagination?.totalPages || 1}
                  {pagination?.total > 0 && ` (${pagination?.total} total)`}
                </span>
                <div className="flex items-center space-x-2">
                  <label className="text-xs text-text-secondary">Rows:</label>
                  <select
                    value={pagination?.size}
                    onChange={e => handleSizeChange(e?.target?.value)}
                    className="h-8 px-2 rounded border border-input bg-background text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    {PAGE_SIZE_OPTIONS?.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handlePageChange(0)}
                  disabled={pagination?.firstPage}
                  className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                  title="First page"
                >
                  <Icon name="ChevronsLeft" size={16} className="text-text-secondary" />
                </button>
                <button
                  onClick={() => handlePageChange(pagination?.page - 1)}
                  disabled={pagination?.firstPage}
                  className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronLeft" size={16} className="text-text-secondary" />
                </button>
                {Array.from({ length: Math.min(5, pagination?.totalPages || 1) }, (_, i) => {
                  const startPage = Math.max(0, Math.min(pagination?.page - 2, (pagination?.totalPages || 1) - 5));
                  const p = startPage + i;
                  if (p >= (pagination?.totalPages || 1)) return null;
                  return (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium ${
                        p === pagination?.page
                          ? 'bg-primary text-primary-foreground'
                          : 'border border-border hover:bg-muted text-text-primary'
                      }`}
                    >
                      {p + 1}
                    </button>
                  );
                })}
                <button
                  onClick={() => handlePageChange(pagination?.page + 1)}
                  disabled={pagination?.lastPage}
                  className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronRight" size={16} className="text-text-secondary" />
                </button>
                <button
                  onClick={() => handlePageChange((pagination?.totalPages || 1) - 1)}
                  disabled={pagination?.lastPage}
                  className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Last page"
                >
                  <Icon name="ChevronsRight" size={16} className="text-text-secondary" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TransactionsPage;
